import type { SupabaseClient } from '../../lib/supabase';
export interface PulseRow {
  day: string;
  page_path: string;
  source: string;
  metrics: Record<string, number>;
  breakdowns: Record<string, unknown>;
}
export interface PulsePayload {
  days: number;
  from: string;
  to: string;
  updated_at: string | null;
  first_day: { ga4: string | null; clarity: string | null };
  rows: PulseRow[];
}
export type PulseErrorCode = 'not_found' | 'bad_request' | 'unreachable';
export class PulseError extends Error {
  constructor(public code: PulseErrorCode) {
    super(code);
  }
}
const messages: Record<PulseErrorCode, string> = {
  not_found: 'This link is no longer active. Ask Kyu for a new one.',
  bad_request: 'This link is not complete. Check it was copied in full.',
  unreachable: 'Page Pulse could not be loaded just now. Try again in a moment.',
};
export const pulseErrorMessage = (code: PulseErrorCode) => messages[code];
function payloadFrom(value: unknown): PulsePayload {
  const raw = value as Partial<PulsePayload> | null;
  return {
    days: typeof raw?.days === 'number' ? raw.days : 28,
    from: typeof raw?.from === 'string' ? raw.from : '',
    to: typeof raw?.to === 'string' ? raw.to : '',
    updated_at: typeof raw?.updated_at === 'string' ? raw.updated_at : null,
    first_day: {
      ga4: typeof raw?.first_day?.ga4 === 'string' ? raw.first_day.ga4 : null,
      clarity: typeof raw?.first_day?.clarity === 'string' ? raw.first_day.clarity : null,
    },
    rows: Array.isArray(raw?.rows)
      ? raw.rows
          .filter(
            (row): row is PulseRow =>
              typeof row === 'object' && row !== null && typeof row.day === 'string',
          )
          .map((row) => ({
            ...row,
            metrics: typeof row.metrics === 'object' && row.metrics !== null ? row.metrics : {},
            breakdowns:
              typeof row.breakdowns === 'object' && row.breakdowns !== null ? row.breakdowns : {},
          }))
      : [],
  };
}
async function call(
  client: SupabaseClient,
  name: 'read_page_pulse' | 'read_page_pulse_team',
  args: Record<string, unknown>,
) {
  const result = await (name === 'read_page_pulse'
    ? client.rpc(name, args as { p_days: number; p_token: string })
    : client.rpc(name, args as { p_days: number }));
  if (result.error) throw new PulseError('unreachable');
  if (result.data === null) throw new PulseError('not_found');
  return payloadFrom(result.data);
}
export const loadPulse = (client: SupabaseClient, token: string, days: number) =>
  call(client, 'read_page_pulse', { p_token: token, p_days: days });
export const loadPulseTeam = (client: SupabaseClient, days: number) =>
  call(client, 'read_page_pulse_team', { p_days: days });
export async function getPulseLink(client: SupabaseClient): Promise<string | null> {
  const { data, error } = await client.rpc('get_page_pulse_link');
  if (error) throw error;
  return typeof data === 'string' ? data : null;
}
export async function rotatePulseLink(client: SupabaseClient): Promise<string> {
  const { data, error } = await client.rpc('rotate_page_pulse_link');
  if (error) throw error;
  if (typeof data !== 'string') throw new Error('The link was not created.');
  return data;
}
export function pulseLinkErrorMessage(cause: unknown) {
  const message = cause instanceof Error ? cause.message : String(cause);
  if (message.includes('not_authorized_for_page_pulse_links'))
    return 'Page Pulse links are TAP admin only.';
  return message === '' ? 'That did not work.' : message;
}
